import {
  AnalyticsMe,
  AnalyticsProduct,
  AnalyticsProvider,
} from "@/common/contracts/analytics.contracts";

const iterableStoreImport = import("@/features/iterable/stores/iterable.store");
const iterableSdkImport = import("@iterable/web-sdk");

/** Helper to safely execute store actions. */
const withStore = async (
  action: (store: Awaited<typeof iterableStoreImport>) => void
) => {
  try {
    const store = await iterableStoreImport;
    action(store);
  } catch (error) {
    console.error("[Iterable provider] Store action failed:", error);
  }
};

/** Helper to safely track events. */
const trackEvent = async (
  eventName: string,
  dataFields: Record<string, unknown>
) => {
  try {
    const { track } = await iterableSdkImport;
    await track({ eventName, dataFields });
  } catch (error) {
    /**
     * Expected: `track` rejects for anonymous users when Unknown User Activation
     * is enabled. Events are stored locally until user activation occurs.
     */
    if (error instanceof Error && error.message.includes("stored locally")) {
      return;
    }

    console.error("[Iterable provider] Track event failed:", error);
  }
};

/** Initialize lazily. */
withStore(store => store.iterable.init());

/**
 * Iterable's implementation of event tracking provider.
 */
export const iterableAnalyticsProvider: AnalyticsProvider = {
  identify: (me: AnalyticsMe) => {
    withStore(store => store.iterableStore.getState().setUserId(me.id));
  },
  trackLogout: () => {
    withStore(store => store.iterableStore.getState().logout());
  },
  trackViewItem: (product: AnalyticsProduct) => {
    trackEvent("view_item", { product });
  },
};
